import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
    Card,
    CircularLoader,
    Divider,
    AlertBar,
    SingleSelect,
    SingleSelectOption, 
} from '@dhis2/ui';
import { Calendar1Icon, ExpandIcon, ViewIcon, UserIcon } from 'lucide-react';
import { useDataEngine, useDataQuery } from '@dhis2/app-runtime';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { useIndicators, useOrgUnit, useDataElements } from './hooks/useData';
import Objectives from './components/Objectives'
import RealTasks from './components/RealTasks'
import RealGoals from './components/RealGoals'
import TaskMetrics from './components/TaskMetrics'
import LeadershipDashboard from './components/LeadershipDashboard'
import ReviewReminders from './components/ReviewReminders'
import KPIKRAReports from './components/KPIKRAReports';
import { usePerformanceHierarchy } from './hooks/usePerformanceHierarchy';
import LeadershipRatingsDashboard from './components/LeadershipRatingsDashboard';
import { useFiscalYear } from '../../shared/FiscalYearContext';

const H4 = ({ children, style }) => (
    <div style={{ fontSize: 24, fontWeight: '700', marginBottom: 24, color: '#212529', ...style }}>
        {children}
    </div>
);
const H5 = ({ children, style }) => (
    <div style={{ fontSize: 20, fontWeight: '600', marginBottom: 16, color: '#343a40', ...style }}>
        {children}
    </div>
);
const BodyText = ({ children, style }) => (
    <div style={{ fontSize: 14, color: '#555', lineHeight: 1.5, ...style }}>
        {children}
    </div>
);

const MetricCard = ({ title, value, subtitle, color = '#007bff' }) => (
    <Card style={{ padding: 16, textAlign: 'center', height: '100%' }}>
        <div style={{ fontSize: 32, fontWeight: '700', color: color, marginBottom: 8 }}>
            {value}
        </div>
        <div style={{ fontSize: 16, fontWeight: '600', color: '#343a40', marginBottom: 4 }}>
            {title}
        </div>
        {subtitle && (
            <div style={{ fontSize: 12, color: '#6c757d' }}>
                {subtitle}
            </div>
        )}
    </Card>
);

const statusColors = {
    'On Track': '#28a745',
    'At Risk': '#ffc107',
    'Behind Schedule': '#dc3545',
};

const DATA_ELEMENT_IDS = {
    goalStart: 'MH7DQVegD1j',
    goalEnd: 'pUvJQ9vy3A3',
    objectiveStart: 'eDcVy661j9e',
    objectiveEnd: 'J0rjxrDKn1l',
    taskWeek: 'EiL3lPaL908',
};

const parseLocalDate = (value) => {
    if (!value) return null;
    
    // Handle string dates
    const dateStr = String(value).split('T')[0];
    const [year, month, day] = dateStr.split('-').map(Number);
    
    if (!year || !month || !day) {
        // Try parsing as date directly
        const d = new Date(value);
        if (!isNaN(d.getTime())) return d;
        return null;
    }
    
    const parsed = new Date(year, month - 1, day);
    if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
    ) {
        return null;
    }
    return parsed;
};

const dateBelongsToFiscalYearRange = (dateValue, fiscalYear) => {
    const date = parseLocalDate(dateValue);
    const fyStart = parseLocalDate(fiscalYear?.start);
    const fyEnd = parseLocalDate(fiscalYear?.end);

    if (!date || !fyStart || !fyEnd) return false;

    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    const normalizedFyStart = new Date(fyStart);
    normalizedFyStart.setHours(0, 0, 0, 0);

    const normalizedFyEnd = new Date(fyEnd);
    normalizedFyEnd.setHours(23, 59, 59, 999);

    return normalizedDate >= normalizedFyStart && normalizedDate <= normalizedFyEnd;
};


const eventBelongsToFiscalYear = (event, eventType, fiscalYear) => {
    if (!fiscalYear?.start || !fiscalYear?.end) return true;

    const fyStart = parseLocalDate(fiscalYear.start);
    const fyEnd = parseLocalDate(fiscalYear.end);
    if (!fyStart || !fyEnd) return true;

    // Helper to get date from dataValues
    const getDateFromDataValues = (dataValues, dataElementId) => {
        if (!dataValues) return null;
        const found = dataValues.find(dv => dv.dataElement === dataElementId);
        return found?.value || null;
    };

    const dataValues = event?.dataValues || [];
    let startDate = '';
    let endDate = '';
    let pointDate = '';

    switch (eventType) {
        case 'goals':
            startDate = getDateFromDataValues(dataValues, DATA_ELEMENT_IDS.goalStart) || event.eventDate;
            endDate = getDateFromDataValues(dataValues, DATA_ELEMENT_IDS.goalEnd) || event.dueDate;
            pointDate = event.eventDate;
            break;
        case 'objectives':
            startDate = getDateFromDataValues(dataValues, DATA_ELEMENT_IDS.objectiveStart) || event.eventDate;
            endDate = getDateFromDataValues(dataValues, DATA_ELEMENT_IDS.objectiveEnd) || event.dueDate;
            pointDate = event.eventDate;
            break;
        case 'tasks':
            pointDate = getDateFromDataValues(dataValues, DATA_ELEMENT_IDS.taskWeek) || event.eventDate;
            break;
        case 'kras':
        case 'kpis':
        case 'leadership':
        default:
            startDate = event.eventDate;
            endDate = event.dueDate;
            pointDate = event.eventDate;
            break;
    }

    // If we have both start and end dates, check if they overlap with fiscal year
    if (startDate && endDate) {
        const rangeStart = parseLocalDate(startDate);
        const rangeEnd = parseLocalDate(endDate);
        if (rangeStart && rangeEnd) {
            const normalizedFyEnd = new Date(fyEnd);
            normalizedFyEnd.setHours(23, 59, 59, 999);
            const normalizedRangeEnd = new Date(rangeEnd);
            normalizedRangeEnd.setHours(23, 59, 59, 999);
            return rangeStart <= normalizedFyEnd && normalizedRangeEnd >= fyStart;
        }
    }

    // Check explicit fiscal year ranges without re-resolving historical years
    if (startDate && dateBelongsToFiscalYearRange(startDate, fiscalYear)) return true;
    if (endDate && dateBelongsToFiscalYearRange(endDate, fiscalYear)) return true;
    if (pointDate && dateBelongsToFiscalYearRange(pointDate, fiscalYear)) return true;

    return false;
};

const DashboardPage = ({ userHasCompanyAccess = true }) => {

    const [currentFilter, setCurrentFilter] = useState('current');
    const engine = useDataEngine();

    const isMounted = useRef(true);

    
    // ============================================================================
    // FETCH RATING SCALE (if needed)
    // ============================================================================

     useEffect(() => {
        // Set mounted flag to true when component mounts
        isMounted.current = true;
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Get fiscal year from context (single source of truth)
    const { fiscalYear, loading: fiscalYearLoading } = useFiscalYear();

    // 1. Get current user
    const { loading: userLoading, error: userError, data: userData } = useDataQuery({
        me: {
            resource: 'me',
            params: { fields: 'id,displayName,organisationUnits[id,name,path,level],userCredentials[userRoles[name]]' }
        }
    });

    const FILTER_OPTIONS = {
        MY_PERFORMANCE: 'my_performance',
        MY_TEAM: 'my_team', 
        DEPARTMENT: 'department',
        ALL: 'all'
    };

    // 2. Extract basic values
    const currentUser = userData?.me;
    const userOrgUnits = userData?.me?.organisationUnits || [];
    const [selectedOrgUnitId, setSelectedOrgUnitId] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState(FILTER_OPTIONS.ALL);
    const [fiscalYearOptions, setFiscalYearOptions] = useState([]);
    const [selectedFiscalYearKey, setSelectedFiscalYearKey] = useState('current');
    const [currentUserPosition, setCurrentUserPosition] = useState(null);

    useEffect(() => {
        if (!selectedOrgUnitId && userOrgUnits.length > 0) {
            setSelectedOrgUnitId(userOrgUnits[0].id);
        }
    }, [userOrgUnits, selectedOrgUnitId]);

    const userOrgUnitId = selectedOrgUnitId || userOrgUnits[0]?.id;
    const userOrgUnitName = userOrgUnits.find(ou => ou.id === userOrgUnitId)?.name;
    const userName = userData?.me?.displayName;

    const [leadershipCompetencies, setLeadershipCompetencies] = useState([]);
    const [loadingCompetencies, setLoadingCompetencies] = useState(false);

    
    // Derive root org unit ID for objective queries (matches ObjectiveList.jsx pattern)
    const rootOrgUnitId = useMemo(() => {
      if (!userOrgUnitId) return userOrgUnitId;
      const currentOrgUnit = userOrgUnits.find(ou => ou.id === userOrgUnitId);
      if (!currentOrgUnit?.path) return userOrgUnitId;
      // const pathParts = currentOrgUnit.path.split('/').filter(Boolean);
      // if (pathParts.length >= 2) return pathParts[1];
      return userOrgUnitId;
    }, [userOrgUnitId, userOrgUnits]);

    const [dashboardData, setDashboardData] = useState([]);
    const [currentFilterExpanded, setCurrentFilterExpanded] = useState(false);

    // 3. Fetch performance data from root org unit so objectives from all accessible org units are included
    const { 
        loading: performanceLoading, 
        error: performanceError, 
        data: latestPerformanceData 
    } = usePerformanceHierarchy(rootOrgUnitId);

    const [lastPerformanceData, setLastPerformanceData] = useState(null);

    useEffect(() => {
        if (latestPerformanceData) {
            setLastPerformanceData(latestPerformanceData);
        }
    }, [latestPerformanceData]);

    const performanceData = latestPerformanceData || lastPerformanceData;

    // 4. Other data fetches
    const { loading: orgLoading, error: orgError, data: orgData } = useOrgUnit(userOrgUnitId);
    const { loading: indicatorsLoading, error: indicatorsError, data: indicatorsData } = useIndicators();
    const { loading: dataElementsLoading, error: dataElementsError, data: dataElementsData } = useDataElements();
    
    useEffect(() => {
        const fetchCompetencies = async () => {
            // ✅ userOrgUnitId is now available
            if (!userOrgUnitId) {
                console.warn('No userOrgUnitId available, skipping competency fetch');
                return;
            }
            setLoadingCompetencies(true);
            try {
                const query = {
                    configEvents: {
                        resource: 'events',
                        params: {
                            program: 'RSANdetbnWC',
                            programStage: 'D0nZ9itkx1D',
                            orgUnit: userOrgUnitId,
                            ouMode: 'ACCESSIBLE',
                            fields: 'event,dataValues[dataElement,value]',
                            pageSize: 100,
                        },
                    },
                };
                const result = await engine.query(query);
                const events = result.configEvents?.events || [];
                const competencies = events
                    .map((event) => {
                        const dataValues = {};
                        event.dataValues?.forEach((dv) => {
                            dataValues[dv.dataElement] = dv.value;
                        });
                        const code = dataValues['code'] || dataValues['name'];
                        const definition = dataValues['definition'] || dataValues['description'];
                        const weight = dataValues['weight'] || dataValues['weightValue'];
                        if (!code) return null;
                        return {
                            id: event.event,
                            name: code,
                            displayContent: definition || code,
                            weight: weight ? Number(weight) : 0.25,
                            behaviouralIndicators: {},
                            description: definition || '',
                        };
                    })
                    .filter(Boolean);
                setLeadershipCompetencies(competencies);
            } catch (error) {
                console.error('Failed to fetch competencies:', error);
            } finally {
                setLoadingCompetencies(false);
            }
        };

        fetchCompetencies();
    }, [userOrgUnitId, engine]); // ✅ Now depends on userOrgUnitId

    // ============================================================
    // 4. FOURTH: Fetch rating scale
    // ============================================================
    const [businessRatingScale, setBusinessRatingScale] = useState(null);
    const [loadingRatingScale, setLoadingRatingScale] = useState(false);

    const getDefaultColor = (rating) => {
        const colors = {
            1: '#dc3545',
            2: '#ff9800',
            3: '#ffc107',
            4: '#4caf50',
            5: '#28a745'
        };
        return colors[rating] || '#6c757d';
    };
    
    // ============================================================================
    // BUILD FISCAL YEAR OPTIONS FROM DATA YEARS + PATTERN FROM CONTEXT
    // ============================================================================

    useEffect(() => {
        // If we don't have fiscalYear from context, use fallback
        if (!fiscalYear?.start || !fiscalYear?.end) {
            const now = new Date();
            const year = now.getFullYear();
            
            // Create at least current and previous year options as fallback
            const fallbackOptions = [
                {
                    value: 'current',
                    label: `FY ${year} - ${year + 1}`,
                    range: {
                        start: `${year}-01-01`,
                        end: `${year + 1}-12-31`
                    }
                },
                {
                    value: `year_${year - 1}`,
                    label: `FY ${year - 1} - ${year}`,
                    range: {
                        start: `${year - 1}-01-01`,
                        end: `${year}-12-31`
                    }
                }
            ];
            setFiscalYearOptions(fallbackOptions);
            setSelectedFiscalYearKey(prev =>
                fallbackOptions.some(option => option.value === prev) ? prev : 'current'
            );
            return;
        }

        try {
            const yearsWithData = new Set();
            const eventCollections = [
                'goals', 'objectives', 'tasks', 'kras', 'kpis', 
                'leadership', 'positionHistory'
            ];

            // If performanceData exists, extract years from it
            if (performanceData) {
                for (const key of eventCollections) {
                    const events = performanceData[key]?.events || [];
                    for (const event of events) {
                        let date = event.eventDate;
                        if (!date && event.dataValues) {
                            const startDV = event.dataValues.find(dv => 
                                dv.dataElement === DATA_ELEMENT_IDS.goalStart ||
                                dv.dataElement === DATA_ELEMENT_IDS.objectiveStart
                            );
                            const taskWeekDV = event.dataValues.find(dv => 
                                dv.dataElement === DATA_ELEMENT_IDS.taskWeek
                            );
                            date = startDV?.value || taskWeekDV?.value || null;
                        }
                        if (date) {
                            const year = new Date(date).getFullYear();
                            if (!isNaN(year) && year > 2000) {
                                yearsWithData.add(year);
                            }
                        }
                    }
                }
            }

            // Always add the current fiscal start year from context
            const currentStartYear = new Date(fiscalYear.start).getFullYear();
            yearsWithData.add(currentStartYear);
            // Also add previous year as fallback
            yearsWithData.add(currentStartYear - 1);

            // If we still have no years, use current year and previous
            if (yearsWithData.size === 0) {
                const now = new Date();
                yearsWithData.add(now.getFullYear());
                yearsWithData.add(now.getFullYear() - 1);
            }

            const sortedYears = Array.from(yearsWithData).sort((a, b) => b - a);

            // Get pattern (month/day) from context
            const startDate = new Date(fiscalYear.start);
            const endDate = new Date(fiscalYear.end);
            const patternStartMonth = startDate.getMonth();
            const patternStartDay = startDate.getDate();
            const patternEndMonth = endDate.getMonth();
            const patternEndDay = endDate.getDate();

            const options = sortedYears.map(year => {
                const isCurrent = year === currentStartYear;
                return {
                    value: isCurrent ? 'current' : `year_${year}`,
                    label: `FY ${year} - ${year + 1}`,
                    range: {
                        start: `${year}-${String(patternStartMonth + 1).padStart(2, '0')}-${String(patternStartDay).padStart(2, '0')}`,
                        end: `${year + 1}-${String(patternEndMonth + 1).padStart(2, '0')}-${String(patternEndDay).padStart(2, '0')}`
                    }
                };
            });

            setFiscalYearOptions(options);

            const hasCurrent = options.some(opt => opt.value === 'current');
            setSelectedFiscalYearKey(prev =>
                options.some(option => option.value === prev)
                    ? prev
                    : hasCurrent ? 'current' : (options[0]?.value || 'current')
            );

            console.log('✅ Fiscal year options built:', options);

        } catch (error) {
            console.warn('Failed to build fiscal year options from data:', error);
            // Fallback: create options from current year
            const now = new Date();
            const year = now.getFullYear();
            setFiscalYearOptions([
                {
                    value: 'current',
                    label: `FY ${year} - ${year + 1}`,
                    range: {
                        start: `${year}-01-01`,
                        end: `${year + 1}-12-31`
                    }
                },
                {
                    value: `year_${year - 1}`,
                    label: `FY ${year - 1} - ${year}`,
                    range: {
                        start: `${year - 1}-01-01`,
                        end: `${year}-12-31`
                    }
                }
            ]);
            setSelectedFiscalYearKey(prev => prev || 'current');
        }
    }, [performanceData, fiscalYear]);

    // Ensure selected key is valid
    useEffect(() => {
        if (fiscalYearOptions.length === 0) return;
        const isValid = fiscalYearOptions.some(opt => opt.value === selectedFiscalYearKey);
        if (!isValid) {
            const currentOption = fiscalYearOptions.find(opt => opt.value === 'current');
            setSelectedFiscalYearKey(currentOption?.value || fiscalYearOptions[0]?.value || 'current');
        }
    }, [fiscalYearOptions, selectedFiscalYearKey]);

    // ============================================================================
    // DERIVED FISCAL YEAR VALUES (EXACTLY AS YOUR ORIGINAL)
    // ============================================================================

    // currentFiscalYear is directly from context
    const currentFiscalYear = fiscalYear;

    // selectedFiscalYear is the range from the selected option
    const selectedFiscalYear = React.useMemo(() => {
        const selected = fiscalYearOptions.find(option => option.value === selectedFiscalYearKey)?.range || currentFiscalYear;
        console.log('📊 Selected Fiscal Year:', {
            selectedFiscalYearKey,
            selectedFiscalYear: selected,
            currentFiscalYear
        });
        return selected;
    }, [fiscalYearOptions, selectedFiscalYearKey, currentFiscalYear]);

    // effectiveFiscalYear – keep your original logic!
    const effectiveFiscalYear = useMemo(() => {
        let result;
        if (currentFilter === 'all') {
            result = null;
        } else if (currentFilter === 'historical' && selectedFiscalYearKey !== 'current') {
            result = selectedFiscalYear;
        } else if (currentFilter === 'current' && selectedFiscalYearKey !== 'current') {
            result = selectedFiscalYear;
        } else {
            result = currentFiscalYear;
        }
        console.log('📊 Effective Fiscal Year:', {
            currentFilter,
            selectedFiscalYearKey,
            result
        });
        return result;
    }, [currentFilter, selectedFiscalYearKey, selectedFiscalYear, currentFiscalYear]);

    // ============================================================================
    // ACCESSIBLE DATA (scope filter) – unchanged from your original
    // ============================================================================

    const extractAttributes = (attributes = []) => {
        const map = {};
        attributes?.forEach(attr => { map[attr.attribute] = attr.value; });
        return map;
    };

    const getDirectReportPositions = (performanceData, currentUserPosition) => {
        if (!currentUserPosition?.trackedEntityInstance || !performanceData?.positions?.trackedEntityInstances) {
            return [];
        }
        const currentPositionId = currentUserPosition.trackedEntityInstance;
        const allPositions = performanceData.positions.trackedEntityInstances;
        const directReports = allPositions.filter(pos => {
            const attrs = extractAttributes(pos.attributes || []);
            return attrs['SjP47qi3JeB'] === currentPositionId;
        });
        return directReports;
    };

    const getUserRoleLevel = (userData, performanceData, currentUserPosition) => {
        const roles = userData?.me?.userCredentials?.userRoles || [];
        const roleNames = roles.map(r => r.name?.toLowerCase() || '');
        if (roleNames.some(r => r.includes('ceo') || r.includes('executive') || r.includes('director'))) return 'executive';
        if (roleNames.some(r => r.includes('hod') || r.includes('head') || r.includes('manager'))) return 'manager';
        if (currentUserPosition?.trackedEntityInstance) {
            const directReports = getDirectReportPositions(performanceData, currentUserPosition);
            if (directReports.length > 0) return 'manager';
        }
        return 'staff';
    };
    const userRoleLevel = getUserRoleLevel(userData, performanceData, currentUserPosition);

    const getDepartmentOrgUnits = (performanceData, rootOrgUnitId) => {
        if (!performanceData?.orgUnits?.organisationUnits) {
            return [rootOrgUnitId].filter(Boolean);
        }
        const allOrgUnits = performanceData.orgUnits.organisationUnits;
        const rootOrgUnit = allOrgUnits.find(ou => ou.id === rootOrgUnitId);
        if (!rootOrgUnit?.path) return [rootOrgUnitId];
        const rootPath = rootOrgUnit.path;
        return allOrgUnits
            .filter(ou => ou.path?.startsWith(rootPath))
            .map(ou => ou.id);
    };

    const getAccessibleOrgUnits = (userOrgUnits, filter, userRoleLevel, performanceData, currentUserPosition) => {
        const currentOrgUnitId = userOrgUnits[0]?.id;
        const positions = performanceData?.positions?.trackedEntityInstances || [];
        const staff = performanceData?.staff?.trackedEntityInstances || [];
        
        switch (filter) {
            case FILTER_OPTIONS.MY_PERFORMANCE:
                return {
                    orgUnits: [currentOrgUnitId].filter(Boolean),
                    positionTeis: [currentUserPosition?.trackedEntityInstance].filter(Boolean),
                    scope: 'self'
                };
            case FILTER_OPTIONS.MY_TEAM:
                const directReportPositions = getDirectReportPositions(performanceData, currentUserPosition);
                const directReportOrgUnits = directReportPositions.map(pos => {
                    const attrs = extractAttributes(pos.attributes || []);
                    const staffId = attrs['V66IFsw5kq6'];
                    const staffMember = staff.find(s => s.trackedEntityInstance === staffId);
                    return staffMember?.orgUnit;
                }).filter(Boolean);
                return {
                    orgUnits: [...new Set(directReportOrgUnits)],
                    positionTeis: directReportPositions.map(p => p.trackedEntityInstance).filter(Boolean),
                    scope: 'team'
                };
            case FILTER_OPTIONS.DEPARTMENT:
                const departmentOrgUnits = getDepartmentOrgUnits(performanceData, currentOrgUnitId);
                const departmentPositions = positions.filter(pos => departmentOrgUnits.includes(pos.orgUnit)) || [];
                return {
                    orgUnits: departmentOrgUnits,
                    positionTeis: departmentPositions.map(p => p.trackedEntityInstance),
                    scope: 'department'
                };
            case FILTER_OPTIONS.ALL:
            default:
                return {
                    orgUnits: userOrgUnits.map(ou => ou.id),
                    positionTeis: positions.map(p => p.trackedEntityInstance),
                    scope: 'all'
                };
        }
    };

    // ============================================================================
    // APPLY FILTERS – KEEP YOUR ORIGINAL LOGIC!
    // ============================================================================

    const accessibleData = useMemo(() => {
        return getAccessibleOrgUnits(
            userOrgUnits, 
            selectedFilter, 
            userRoleLevel, 
            performanceData,
            currentUserPosition
        );
    }, [userOrgUnits, selectedFilter, userRoleLevel, performanceData, currentUserPosition]);

    const accessiblePerformanceData = useMemo(() => {
        if (!performanceData) return null;

        const orgUnitSet = new Set(accessibleData.orgUnits || []);
        const positionTeiSet = new Set(accessibleData.positionTeis || []);
        const taskPositionElementId = 'SlgO60cMK3F';

        const filterEventsByOrgUnits = (collection) => ({
            ...collection,
            events: (collection?.events || []).filter(event =>
                accessibleData.scope === 'all' || orgUnitSet.has(event.orgUnit)
            )
        });

        const filterObjectivesByPosition = (collection) => ({
            ...collection,
            events: (collection?.events || []).filter(event =>
                accessibleData.scope === 'all' ||
                positionTeiSet.size === 0 ||
                positionTeiSet.has(event.trackedEntityInstance)
            )
        });

        const filterTasks = (collection) => ({
            ...collection,
            events: (collection?.events || []).filter(event => {
                if (accessibleData.scope === 'all') return true;
                const taskPositionTei = event.dataValues?.find(
                    dv => dv.dataElement === taskPositionElementId
                )?.value;
                return orgUnitSet.has(event.orgUnit) ||
                    (taskPositionTei && positionTeiSet.has(taskPositionTei));
            })
        });

        return {
            ...performanceData,
            goals: performanceData.goals,
            objectives: filterObjectivesByPosition(performanceData.objectives),
            tasks: filterTasks(performanceData.tasks),
            kras: filterEventsByOrgUnits(performanceData.kras),
            kpis: filterEventsByOrgUnits(performanceData.kpis),
            positionHistory: filterEventsByOrgUnits(performanceData.positionHistory),
        };
    }, [performanceData, accessibleData]);

    useEffect(() => {
        console.log('📊 filteredByFiscalYear dependencies changed:', {
            hasAccessibleData: !!accessiblePerformanceData,
            currentFilter,
            selectedFiscalYearKey,
            selectedFiscalYear,
            currentFiscalYear
        });
    }, [accessiblePerformanceData, currentFilter, selectedFiscalYearKey, selectedFiscalYear, currentFiscalYear]);

    // ================== ORIGINAL filteredByFiscalYear (unchanged) ==================
    const filteredByFiscalYear = useMemo(() => {

       if (!isMounted.current) {
        console.log('📊 Component unmounted, skipping filter');
        return null;
        }

        console.log('📊 Running filteredByFiscalYear memo...');

        // Wait for data to be available
        if (!accessiblePerformanceData) {
            console.log('📊 No accessiblePerformanceData available');
            return null;
        }
        
        // Wait for data to actually be loaded (has events)
        const hasData = accessiblePerformanceData.goals?.events?.length > 0 || 
                        accessiblePerformanceData.objectives?.events?.length > 0 ||
                        accessiblePerformanceData.tasks?.events?.length > 0;
        
        if (!hasData && performanceLoading) {
            console.log('📊 Data is still loading, returning null');
            return null;
        }
        
        // If no data at all, return null
        if (!hasData) {
            console.log('📊 Accessible data has no events, returning null');
            return null;
        }
        
        // If no fiscal year to use, return all data
        let fiscalYearToUse = null;
        
        if (currentFilter === 'all') {
            console.log('📊 Filter mode: ALL - showing all data');
            // For 'all' mode, return the data without filtering
            return accessiblePerformanceData;
        }
        
        if (selectedFiscalYearKey !== 'current') {
            fiscalYearToUse = selectedFiscalYear;
        } else {
            fiscalYearToUse = currentFiscalYear;
        }
        
        if (!fiscalYearToUse?.start || !fiscalYearToUse?.end) {
            console.log('📊 No fiscal year available, showing all data');
            return accessiblePerformanceData;
        }
        
        console.log('📊 Filtering data with fiscal year:', {
            fiscalYearToUse,
            currentFilter,
            selectedFiscalYearKey,
            goalsCount: accessiblePerformanceData.goals?.events?.length || 0,
            objectivesCount: accessiblePerformanceData.objectives?.events?.length || 0
        });
        
        const applyFilter = (collection, eventType) => {
            if (!collection?.events) return collection;

            const filteredEvents = collection.events.filter(event => {
                if (currentFilter === 'all') return true;
                // For current mode or any other mode, use the selected fiscal year
                return eventBelongsToFiscalYear(event, eventType, fiscalYearToUse);
            });

            console.log(`📊 ${eventType} filtered: ${collection.events.length} -> ${filteredEvents.length}`);
            return { ...collection, events: filteredEvents };
        };
        
        const result = {
            ...accessiblePerformanceData,
            goals: applyFilter(accessiblePerformanceData.goals, 'goals'),
            objectives: applyFilter(accessiblePerformanceData.objectives, 'objectives'),
            tasks: applyFilter(accessiblePerformanceData.tasks, 'tasks'),
            kras: applyFilter(accessiblePerformanceData.kras, 'kras'),
            kpis: applyFilter(accessiblePerformanceData.kpis, 'kpis'),
            positionHistory: applyFilter(accessiblePerformanceData.positionHistory, 'positionHistory'),
        };
        
        console.log('📊 Filtered results:', {
            goals: result.goals?.events?.length || 0,
            objectives: result.objectives?.events?.length || 0,
            tasks: result.tasks?.events?.length || 0,
            kpis: result.kpis?.events?.length || 0,
            filterMode: currentFilter,
            fiscalYear: selectedFiscalYearKey
        });
        
        return result;
    }, [accessiblePerformanceData, currentFilter, currentFiscalYear,
         selectedFiscalYearKey, selectedFiscalYear, performanceLoading, isMounted]);

    useEffect(() => {
        console.log('🔄 Filter changed, forcing update:', {
            currentFilter,
            selectedFiscalYearKey,
            hasData: !!filteredByFiscalYear
        });
        // This effect doesn't do anything except log, but it ensures the component re-renders
        // when filters change because it depends on the same values as filteredByFiscalYear
    }, [currentFilter, selectedFiscalYearKey]);

    // ============================================================================
    // LOAD USER POSITION (unchanged)
    // ============================================================================

    useEffect(() => {
        const loadPosition = async () => {
            if (!currentUser?.id || !engine) return;
            try {
                const response = await engine.query({
                    currentUserPosition: {
                        resource: 'trackedEntityInstances',
                        params: {
                            trackedEntityType: 'UGGo0Ebjl0E',
                            filter: `V66IFsw5kq6:eq:${currentUser.id}`,
                            fields: 'trackedEntityInstance,attributes[attribute,value]',
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1
                        }
                    }
                });
                
                // Check if component is still mounted before setting state
                if (isMounted.current) {
                    const position = response.currentUserPosition?.trackedEntityInstances?.[0];
                    if (position) {
                        setCurrentUserPosition(position);
                    }
                }
            } catch (error) {
                console.error('❌ [POSITION] Failed to load position:', error);
            }
        };
        loadPosition();
    }, [currentUser?.id, engine, isMounted]);


    const effectivePerformanceData = useMemo(() => {
        // If data is still loading, return null
        if (performanceLoading) {
            console.log('📊 Performance data is loading');
            return null;
        }
        
        // If no accessible data, return null
        if (!accessiblePerformanceData) {
            console.log('📊 No accessible data');
            return null;
        }
        
        // Use filtered data if available, otherwise use accessible data
        const result = filteredByFiscalYear || accessiblePerformanceData;
        
        console.log('📊 effectivePerformanceData:', {
            hasFiltered: !!filteredByFiscalYear,
            hasAccessible: !!accessiblePerformanceData,
            resultKeys: result ? Object.keys(result) : 'null'
        });
        
        return result;
    }, [filteredByFiscalYear, accessiblePerformanceData, performanceLoading]);

    // ============================================================================
    // LOADING / ERROR STATES
    // ============================================================================

    // Check if we have enough data to render
    const isDataReady = useMemo(() => {
        if (performanceLoading || !performanceData) return false;
        
        // Check if we have at least one type of data
        const hasEvents = performanceData.goals?.events?.length > 0 ||
                        performanceData.objectives?.events?.length > 0 ||
                        performanceData.tasks?.events?.length > 0;
        
        return hasEvents;
    }, [performanceData, performanceLoading]);

    if (userLoading || fiscalYearLoading) {
        return (
            <div style={{ padding: 24 }}>
                <CircularLoader small />
                <BodyText style={{ textAlign: 'center', marginTop: 16 }}>
                    Loading your dashboard...
                </BodyText>
            </div>
        );
    }

    if (userError || !userOrgUnitId) {
        return (
            <div style={{ padding: 24 }}>
                <AlertBar critical>
                    {userError?.message || 'Unable to determine your organization unit. Please contact administrator.'}
                </AlertBar>
            </div>
        );
    }

    if (userLoading || fiscalYearLoading) {
        return (
            <div style={{ padding: 24 }}>
                <CircularLoader small />
                <BodyText style={{ textAlign: 'center', marginTop: 16 }}>
                    Loading your dashboard...
                </BodyText>
            </div>
        );
    }

    if (orgLoading || indicatorsLoading || performanceLoading || !isDataReady) {
        return (
            <div style={{ padding: 24 }}>
                <CircularLoader small />
                <BodyText style={{ textAlign: 'center', marginTop: 16 }}>
                    Loading dashboard data for {userOrgUnitName}...
                </BodyText>
            </div>
        );
    }

    if (orgError || indicatorsError || performanceError) {
        return (
            <div style={{ padding: 24 }}>
                <AlertBar critical>
                    Error loading dashboard data: {orgError?.message || indicatorsError?.message || performanceError?.message}
                </AlertBar>
            </div>
        );
    }

    // ============================================================================
    // FILTER SELECTOR (unchanged)
    // ============================================================================

    const FilterSelector = ({ currentFilter, onFilterChange, userRoleLevel, orgUnitName }) => {
        const availableFilters = React.useMemo(() => {
            const filters = [
                { 
                    value: FILTER_OPTIONS.MY_PERFORMANCE, 
                    label: '👤 My Performance', 
                    roles: ['staff', 'manager', 'executive'],
                    description: 'View only your own objectives and tasks'
                },
                { 
                    value: FILTER_OPTIONS.MY_TEAM, 
                    label: '👥 Team Performance', 
                    roles: ['manager', 'executive'],
                    description: 'View your direct reports\' performance'
                },
                { 
                    value: FILTER_OPTIONS.DEPARTMENT, 
                    label: `🏢 ${orgUnitName || 'Department'} Department`, 
                    roles: ['manager', 'executive'],
                    description: 'View entire department performance'
                },
                { 
                    value: FILTER_OPTIONS.ALL, 
                    label: '🌐 Overall Company Performance', 
                    roles: ['staff', 'manager', 'executive'],
                    description: 'Clear the current scope filter'
                }
            ];
            return filters.filter(f => f.roles.includes(userRoleLevel));
        }, [userRoleLevel, orgUnitName]);

        if (availableFilters.length === 0) return null;

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap'
            }}>
                {availableFilters.map(filter => (
                    <button
                        key={filter.value}
                        onClick={() => onFilterChange(filter.value)}
                        style={{
                            padding: '6px 14px',
                            borderRadius: '999px',
                            fontSize: '0.8rem',
                            fontWeight: currentFilter === filter.value ? 600 : 400,
                            backgroundColor: currentFilter === filter.value
                                ? 'rgba(255,255,255,0.22)'
                                : 'rgba(255,255,255,0.07)',
                            color: currentFilter === filter.value ? '#ffffff' : 'rgba(255,255,255,0.65)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: `1px solid ${currentFilter === filter.value ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.18)'}`,
                            boxShadow: currentFilter === filter.value ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
                        }}
                        onMouseEnter={(e) => {
                            if (currentFilter !== filter.value) {
                                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)';
                                e.currentTarget.style.color = '#ffffff';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (currentFilter !== filter.value) {
                                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)';
                                e.currentTarget.style.color = 'rgba(255,255,255,0.65)';
                            }
                        }}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>
        );
    };

    // ============================================================================
    // RENDER
    // ============================================================================
    
    return (
        <div style={{ 
            padding: '10px', 
            marginTop: '1px', 
            fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
            minHeight: '100vh'
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 8,
                flexWrap: 'wrap',
                gap: '12px'
            }}>
                <h3 style={{ color: '#054382ff', margin: 0 }}>Dashboard</h3>
                <div style={{ textAlign: 'right' }}>
                    <h3 style={{ color: '#054382ff', margin: 0, fontSize: '1.1rem' }}>{userOrgUnitName}</h3>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Welcome, {userName}</div>
                </div>
            </div>

            <hr style={{ width: '100%', border: '1.5px solid #c0c3c5ff', margin: '8px 0 16px 0' }} />
            
            {/* Filter Bar – exactly as your original, but using effectiveFiscalYear for the label */}
            <div
                style={{
                    position: 'sticky',
                    top: '10px',
                    zIndex: 100,
                    marginBottom: '20px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <div
                    style={{
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(5, 67, 130, 0.25)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        width: currentFilterExpanded ? '450px' : 'auto',
                        minWidth: currentFilterExpanded ? '380px' : '140px',
                        maxWidth: currentFilterExpanded ? '450x' : 'auto',
                        transition: 'width 0.25s ease, min-width 0.25s ease', 
                        direction: 'ltr',
                    }} >
                    <div style={{
                        background: 'linear-gradient(135deg, #208205 0%, #a2c205 100%)',
                        padding: currentFilterExpanded ? '12px 16px' : '8px 14px',
                        borderRadius: '12px',
                        transition: 'padding 0.25s ease, min-height 0.25s ease',
                        cursor: 'pointer',
                        minHeight: currentFilterExpanded ? 'auto' : '20px',
                    }}
                    onMouseEnter={() => setCurrentFilterExpanded(true)}
                    onMouseLeave={() => setCurrentFilterExpanded(false)}
                    >
                        {/* Collapsed View */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 10,
                            whiteSpace: 'nowrap',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Calendar1Icon size={14} style={{ color: '#ffffff' }} />
                                <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: '#ffffff',
                                }}>
                                    {currentFiscalYear ? `${new Date(currentFiscalYear.start).getFullYear()} - ${new Date(currentFiscalYear.end).getFullYear()}` : 'Loading...'}
                                </span>
                            </div>
                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>|</span>
                            {(() => {
                                const hasActiveFilters = selectedFilter !== FILTER_OPTIONS.ALL || 
                                                        selectedFiscalYearKey !== 'current' || 
                                                        currentFilter === 'all';
                                if (!hasActiveFilters) {
                                    return (
                                        <span style={{
                                            fontSize: '0.7rem',
                                            color: '#ffffff',
                                            fontWeight: 500,
                                            backgroundColor: 'rgba(255,255,255,0.15)',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                        }}>
                                            Current FY
                                        </span>
                                    );
                                } else if (currentFilter === 'all') {
                                    return (
                                        <span style={{
                                            fontSize: '0.7rem',
                                            color: '#ffffff',
                                            fontWeight: 500,
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                        }}>
                                            All Data
                                        </span>
                                    );
                                } else if (selectedFiscalYearKey !== 'current') {
                                    const fyOption = fiscalYearOptions.find(o => o.value === selectedFiscalYearKey);
                                    return (
                                        <span style={{
                                            fontSize: '0.7rem',
                                            color: '#ffffff',
                                            fontWeight: 500,
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                        }}>
                                            {fyOption?.label || 'Selected FY'}
                                        </span>
                                    );
                                } else if (selectedFilter !== FILTER_OPTIONS.ALL) {
                                    return (
                                        <span style={{
                                            fontSize: '0.7rem',
                                            color: '#ffffff',
                                            fontWeight: 500,
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                        }}>
                                            {selectedFilter === FILTER_OPTIONS.MY_PERFORMANCE ? 'My Performance' : 
                                            selectedFilter === FILTER_OPTIONS.MY_TEAM ? 'My Team' : 
                                            `${userOrgUnitName?.split(' ')[0] || 'Dept'} Dept`}
                                        </span>
                                    );
                                }
                                return null;
                            })()}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: 'rgba(255,255,255,0.5)' }}>
                                <ExpandIcon size={12} />
                            </div>
                        </div>

                        {/* Expanded View */}
                        {currentFilterExpanded && (
                            <div style={{
                                marginTop: '14px',
                                paddingTop: '14px',
                                borderTop: '1px solid rgba(255,255,255,0.2)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{
                                        fontWeight: 600,
                                        color: '#ffffff',
                                        fontSize: '0.7rem',
                                        textTransform: 'uppercase',
                                        minWidth: '30px',
                                    }}>
                                        FY:
                                    </span>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {fiscalYearOptions.map(option => (
                                            <button
                                                key={option.value}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedFiscalYearKey(option.value);
                                                    if (option.value !== 'current') {
                                                        setCurrentFilter('historical');
                                                    } else {
                                                        setCurrentFilter('current');
                                                    }
                                                }}
                                                style={{
                                                    padding: '3px 10px',
                                                    borderRadius: '16px',
                                                    fontSize: '0.7rem',
                                                    fontWeight: selectedFiscalYearKey === option.value ? 600 : 400,
                                                    backgroundColor: selectedFiscalYearKey === option.value ? '#ffffff' : 'rgba(255,255,255,0.15)',
                                                    color: selectedFiscalYearKey === option.value ? '#208205' : '#ffffff',
                                                    cursor: 'pointer',
                                                    border: 'none',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                {option.label.replace('FY ', '')}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{
                                        fontWeight: 600,
                                        color: '#ffffff',
                                        fontSize: '0.7rem',
                                        textTransform: 'uppercase',
                                        minWidth: '30px',
                                    }}>
                                        Scope:
                                    </span>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {[
                                            { value: FILTER_OPTIONS.MY_PERFORMANCE, label: '👤 My Performance' },
                                            { value: FILTER_OPTIONS.MY_TEAM, label: '👥 My Team' },
                                            { value: FILTER_OPTIONS.DEPARTMENT, label: `🏢 ${userOrgUnitName?.split(' ')[0] || 'Dept'} Dept` },
                                            { value: FILTER_OPTIONS.ALL, label: '🌐 All' },
                                        ].filter(filter => {
                                            if (filter.value === FILTER_OPTIONS.MY_TEAM && userRoleLevel !== 'manager' && userRoleLevel !== 'executive') return false;
                                            if (filter.value === FILTER_OPTIONS.DEPARTMENT && userRoleLevel !== 'manager' && userRoleLevel !== 'executive') return false;
                                            return true;
                                        }).map(filter => (
                                            <button
                                                key={filter.value}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedFilter(filter.value);
                                                }}
                                                style={{
                                                    padding: '3px 10px',
                                                    borderRadius: '16px',
                                                    fontSize: '0.7rem',
                                                    fontWeight: selectedFilter === filter.value ? 600 : 400,
                                                    backgroundColor: selectedFilter === filter.value ? '#ffffff' : 'rgba(255,255,255,0.15)',
                                                    color: selectedFilter === filter.value ? '#208205' : '#ffffff',
                                                    cursor: 'pointer',
                                                    border: 'none',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                {filter.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{
                                        fontWeight: 600,
                                        color: '#ffffff',
                                        fontSize: '0.7rem',
                                        textTransform: 'uppercase',
                                        minWidth: '30px',
                                    }}>
                                        Mode:
                                    </span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (currentFilter === 'all') {
                                                setCurrentFilter('current');
                                            } else {
                                                setCurrentFilter('all');
                                            }
                                        }}
                                        style={{
                                            padding: '3px 12px',
                                            borderRadius: '16px',
                                            fontSize: '0.7rem',
                                            fontWeight: currentFilter === 'all' ? 600 : 400,
                                            backgroundColor: currentFilter === 'all' ? '#ffffff' : 'rgba(255,255,255,0.15)',
                                            color: currentFilter === 'all' ? '#208205' : '#ffffff',
                                            cursor: 'pointer',
                                            border: 'none',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {currentFilter === 'all' ? '✓ All Data Mode' : 'All Data (Show Everything)'}
                                    </button>
                                </div>

                                {(selectedFilter !== FILTER_OPTIONS.ALL || selectedFiscalYearKey !== 'current' || currentFilter === 'all') && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        padding: '6px 10px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                        borderRadius: '8px',
                                        fontSize: '0.65rem',
                                        color: 'rgba(255,255,255,0.95)',
                                        marginTop: '4px',
                                    }}>
                                        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            <span>🎯 Active:</span>
                                            {currentFilter === 'all' && <span>All Data</span>}
                                            {selectedFiscalYearKey !== 'current' && (
                                                <span>{fiscalYearOptions.find(o => o.value === selectedFiscalYearKey)?.label}</span>
                                            )}
                                            {selectedFilter !== FILTER_OPTIONS.ALL && (
                                                <span>{selectedFilter === FILTER_OPTIONS.MY_PERFORMANCE ? 'My Performance' : 
                                                    selectedFilter === FILTER_OPTIONS.MY_TEAM ? 'My Team' : 
                                                    `${userOrgUnitName?.split(' ')[0]} Dept`}</span>
                                            )}
                                        </span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCurrentFilter('current');
                                                setSelectedFilter(FILTER_OPTIONS.ALL);
                                                setSelectedFiscalYearKey('current');
                                            }}
                                            style={{
                                                background: 'rgba(255,255,255,0.2)',
                                                border: 'none',
                                                color: '#ffffff',
                                                cursor: 'pointer',
                                                fontSize: '0.6rem',
                                                borderRadius: '8px',
                                                padding: '2px 8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                            }}
                                        >
                                            ✕ Clear
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ===== SECTIONS ===== */}
            {isDataReady && effectivePerformanceData && (
            <>
            <ReviewReminders
                orgUnitId={userOrgUnitId}
                performanceData={effectivePerformanceData}
                currentFilter={currentFilter}
                selectedFiscalYear={effectiveFiscalYear || fiscalYear}
            />

            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, marginTop: '24px' }}> 
                <h3 style={{ margin: 0 }}>Goals Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />
            <RealGoals 
                orgUnitId={userOrgUnitId} 
                performanceData={effectivePerformanceData}
                accessibleOrgUnitIds={accessibleData.orgUnits}
                positionTeis={accessibleData.positionTeis}  
                filterScope={accessibleData.scope}
                selectedFiscalYear={effectiveFiscalYear}
                currentFilter={currentFilter}
            />

            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, marginTop: '24px' }}> 
                <h3 style={{ margin: 0 }}>Objectives Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />
            <Objectives 
                orgUnitId={userOrgUnitId}
                currentUser={{ name: userName, id: userData?.me?.id }}
                performanceData={effectivePerformanceData}
                accessibleOrgUnitIds={accessibleData.orgUnits}
                positionTeis={accessibleData.positionTeis}  
                filterScope={accessibleData.scope}          
            />

            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, marginTop: '24px' }}> 
                <h3 style={{ margin: 0 }}>Key Performance Indicators Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />
            <KPIKRAReports
                orgUnitId={userOrgUnitId}
                performanceData={effectivePerformanceData}
                accessibleOrgUnitIds={accessibleData.orgUnits} 
                filterScope={accessibleData.scope}            
            /> 
            </>
           )}
            {/*// ===== TOUCH POINTS SECTION ===== */}
                <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: '24px' }}> 
                    <h3 style={{ margin: 0 }}>Touch Points Section</h3>
                </div>
                <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} /> 

                {/* Task Metrics Summary Cards */}
                <TaskMetrics
                    orgUnitId={userOrgUnitId}
                    performanceData={effectivePerformanceData}
                    accessibleOrgUnitIds={accessibleData.orgUnits} 
                    filterScope={accessibleData.scope}
                />

                {/* Real Tasks List */}
                <div style={{ marginTop: 24 }}>
                    <RealTasks 
                        orgUnitId={userOrgUnitId}
                        performanceData={effectivePerformanceData}
                        accessibleOrgUnitIds={accessibleData.orgUnits}
                        positionTeis={accessibleData.positionTeis}
                        filterScope={accessibleData.scope}
                    />
                </div>

            {/* Leadership and Behaviors Section */}
            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: '24px' }}> 
                    <h3 style={{ margin: 0 }}>Leadership and Behaviors Section</h3>
                </div>
                <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />

                {console.log('🔍 ALL AVAILABLE DATA KEYS:', {
                    keys: effectivePerformanceData ? Object.keys(effectivePerformanceData) : 'no data',
                    sampleData: effectivePerformanceData ? {
                        hasObjectives: !!effectivePerformanceData.objectives,
                        hasTasks: !!effectivePerformanceData.tasks,
                        hasKpis: !!effectivePerformanceData.kpis,
                        hasKras: !!effectivePerformanceData.kras,
                        hasGoals: !!effectivePerformanceData.goals,
                    } : 'none'
                })}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', marginTop: '10px', marginBottom: 24 }}>
                    <LeadershipDashboard
                        orgUnitId={userOrgUnitId}
                        accessibleOrgUnitIds={accessibleData?.orgUnits || []}
                        filterScope={accessibleData?.scope || 'all'}
                    />
                </div>

                {/* ===== LEADERSHIP RATINGS DASHBOARD - FIXED PROPS ===== */}
                <LeadershipRatingsDashboard
                    orgUnitId={userOrgUnitId}
                    performanceData={effectivePerformanceData}
                    accessibleOrgUnitIds={accessibleData?.orgUnits || []}
                    filterScope={accessibleData?.scope || 'all'}
                    competencies={leadershipCompetencies}
                    ratingScale={businessRatingScale}
                    currentFilter={currentFilter} // Pass the current filter mode
                    selectedFiscalYear={selectedFiscalYear} // Pass the selected fiscal year
                    fiscalYear={fiscalYear} // Pass the current fiscal year from context
                />
        </div>
    );
};

export default DashboardPage;
